import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Layout, FileText, Code as CodeIcon, Braces, Save, Loader2, Database, AlertCircle, CheckCircle2 } from 'lucide-react';
import { chartsData } from '../data/chartsData';
import { isCircularChartType } from '../utils/chartConfigMerge';
import { getChartLibrarySettings, putChartLibrarySettings, getDatabases } from '../services/api';
import { Header } from '../components/layout/Header';
import { WriteGuard, ReadOnlyBanner } from '../components/auth/WriteGuard';
import { useAuth } from '../context/AuthContext';
import '../utils/reportChartSetup';
import { CHART_LIBRARY_TABLE_CLASS } from '../utils/reportTableTokens';

const editorTextareaClass =
    'absolute inset-0 w-full h-full px-6 pb-4 bg-transparent text-gray-300 font-mono text-[13px] leading-relaxed resize-none outline-none focus:ring-0 selection:bg-primary-500/30';

// Presentational cards (HeroCard, AnalyticsOverviewCard, HeatmapChart) are
// driven by `props` instead of a chart.js `data` object.
const isPropsDrivenChart = (chart) => Boolean(chart && chart.props);

const componentDisplayName = (chart) =>
    chart?.component?.displayName || chart?.component?.name || 'Chart';

// Live-generated component usage (no static snippets stored in the catalog).
const buildComponentCode = (chart, propsDriven) => {
    const name = componentDisplayName(chart);
    if (propsDriven) {
        return `import { ${name} } from '../components/charts';

// Renders from the props in the Data section below.
export const Example = () => (
  <${name} {...props} />
);
`;
    }
    return `import { ${name} } from '../components/charts';

// Renders from the data & options in the Data section below.
export const Example = () => (
  <${name} data={data} options={options} />
);
`;
};

// Deep-clone dropping function values (e.g. lucide icon components) so the
// object can be shown as editable JSON in the Data block.
const stripFunctions = (value) => {
    if (Array.isArray(value)) return value.map(stripFunctions);
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            if (typeof v === 'function') continue;
            out[k] = stripFunctions(v);
        }
        return out;
    }
    return value;
};

// Merge edited JSON over the original, keeping non-JSON values (icon
// components) from the original wherever the edit doesn't supply them.
const mergePreserveFunctions = (orig, edited) => {
    if (Array.isArray(edited)) {
        const origArr = Array.isArray(orig) ? orig : [];
        return edited.map((item, i) => mergePreserveFunctions(origArr[i], item));
    }
    if (edited && typeof edited === 'object') {
        const origObj = orig && typeof orig === 'object' && !Array.isArray(orig) ? orig : {};
        const out = { ...origObj };
        for (const [k, v] of Object.entries(edited)) {
            out[k] = mergePreserveFunctions(origObj[k], v);
        }
        return out;
    }
    if (edited === undefined) return orig;
    return edited;
};

// JSON the Data block shows: the inputs the component receives.
const previewToDataText = (cfg, propsDriven) => {
    if (!cfg) return '';
    const view = propsDriven
        ? stripFunctions(cfg.props || {})
        : { data: cfg.data, options: cfg.options };
    return JSON.stringify(view, null, 2);
};

export const ChartDetail = () => {
    const { t } = useTranslation(['pages-misc-3', 'common']);
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const databaseId = searchParams.get('database_id');

    const chart = chartsData.find(c => c.id === id);
    // Editors/admins may edit & save; viewers get a read-only view.
    const { canWrite } = useAuth();
    const [activeTab, setActiveTab] = useState('data');
    const [previewConfig, setPreviewConfig] = useState(null);
    const [componentCode, setComponentCode] = useState('');
    const [dataText, setDataText] = useState('');
    const [dataError, setDataError] = useState('');
    const propsDriven = isPropsDrivenChart(chart);
    const [reportSettingsMap, setReportSettingsMap] = useState({});
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [reportSaveState, setReportSaveState] = useState('idle');
    const [databases, setDatabases] = useState([]);
    const [databasesLoading, setDatabasesLoading] = useState(true);

    const libraryHref = databaseId
        ? `/settings?tab=charts&database_id=${encodeURIComponent(databaseId)}`
        : '/settings?tab=charts';

    useEffect(() => {
        if (!chart) return;
        const initial = {
            data: chart.data,
            options: chart.options,
            component: chart.component,
            props: chart.props,
        };
        setComponentCode(buildComponentCode(chart, isPropsDrivenChart(chart)));
        setPreviewConfig(initial);
        setDataText(previewToDataText(initial, isPropsDrivenChart(chart)));
        setDataError('');
    }, [chart]);

    useEffect(() => {
        if (!databaseId || !chart) return;
        let cancelled = false;
        setSettingsLoading(true);
        (async () => {
            try {
                const res = await getChartLibrarySettings(databaseId);
                const settings = res.data?.data?.settings ?? {};
                if (!cancelled) {
                    setReportSettingsMap(typeof settings === 'object' && settings !== null ? settings : {});
                }
            } catch {
                if (!cancelled) setReportSettingsMap({});
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [databaseId, chart]);

    useEffect(() => {
        let cancelled = false;
        setDatabasesLoading(true);
        (async () => {
            try {
                const res = await getDatabases();
                const list = res.data?.data?.databases ?? [];
                if (!cancelled) setDatabases(Array.isArray(list) ? list : []);
            } catch {
                if (!cancelled) setDatabases([]);
            } finally {
                if (!cancelled) setDatabasesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleDatabaseChange = useCallback(
        (e) => {
            const v = e.target.value;
            const next = new URLSearchParams(searchParams);
            if (v) next.set('database_id', v);
            else next.delete('database_id');
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const handleReportNoteChange = useCallback((value) => {
        if (!chart) return;
        setReportSettingsMap((prev) => ({ ...prev, [chart.id]: value }));
    }, [chart]);

    const handleSaveReportNotes = async () => {
        if (!databaseId || !chart) return;
        setReportSaveState('saving');
        try {
            await putChartLibrarySettings(databaseId, { settings: reportSettingsMap });
            setReportSaveState('saved');
            // Redirect back to library after a brief moment to show "Saved" state
            setTimeout(() => {
                navigate(libraryHref);
            }, 800);
        } catch {
            setReportSaveState('error');
        }
    };

    // Live-apply edited Data-block JSON to the preview.
    const handleDataChange = useCallback((text) => {
        setDataText(text);
        let parsed;
        try {
            parsed = JSON.parse(text);
        } catch (e) {
            setDataError(e.message);
            return;
        }
        setDataError('');
        setPreviewConfig((prev) => {
            if (!prev) return prev;
            if (propsDriven) {
                return { ...prev, props: mergePreserveFunctions(chart.props, parsed) };
            }
            return {
                ...prev,
                data: parsed.data ?? prev.data,
                options: parsed.options ?? prev.options,
            };
        });
    }, [chart, propsDriven]);

    if (!chart || !previewConfig) {
        return (
            <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
                <Header />
                <div className="flex flex-1 flex-col items-center justify-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('chartDetail.chartNotFound')}</h2>
                    <button
                        onClick={() =>
                            navigate(
                                databaseId
                                    ? `/chart-library?database_id=${encodeURIComponent(databaseId)}`
                                    : '/chart-library'
                            )
                        }
                        className="px-6 py-2 bg-primary-600 text-white rounded-xl"
                    >
                        {t('chartDetail.backToLibrary')}
                    </button>
                </div>
            </div>
        );
    }

    const ChartComp = previewConfig.component;
    const isCircular = isCircularChartType(chart.type);
    const isTable = String(chart.type || '').toUpperCase() === 'TABLE';
    const catalogProps = chart.props || {};
    const previewProps = propsDriven
        ? mergePreserveFunctions(catalogProps, previewConfig.props || {})
        : null;

    return (
        <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
            <Header />
            <div className="flex-1 overflow-auto bg-layout-bg">
                <div className="p-4 sm:p-8">
            <div className="mb-6 sm:mb-8">
                <button
                    onClick={() => navigate(libraryHref)}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-2 sm:mb-3 group"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs sm:text-sm font-medium">{t('chartDetail.backToLibrary')}</span>
                </button>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                            {chart.title}
                        </h1>
                        <div className="text-[10px] font-bold tracking-wider text-gray-400 bg-white border border-gray-100 px-3 py-1 rounded-md uppercase">
                            {chart.type}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-8 items-start">
                <div className="xl:col-span-5 bg-editor-bg rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col h-[600px] transition-all duration-300">
                    <div className="bg-editor-header px-4 py-3 flex items-center justify-between border-b border-white/5">
                        <div className="flex bg-white/5 p-1 rounded-xl gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveTab('code')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'code' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-300'}`}
                            >
                                <CodeIcon className="w-3.5 h-3.5" />
                                {t('chartDetail.tabs.code')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('data')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'data' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-300'}`}
                            >
                                <Braces className="w-3.5 h-3.5" />
                                {t('chartDetail.tabs.data')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('settings')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'settings' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-300'}`}
                            >
                                <FileText className="w-3.5 h-3.5" />
                                {t('common:settings')}
                            </button>
                        </div>

                        {activeTab === 'data' ? (
                            dataError ? (
                                <span className="flex items-center gap-1.5 px-3 py-2 bg-danger/10 text-danger rounded-lg text-xs font-bold border border-danger/20">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    {t('chartDetail.invalidJson')}
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5 px-3 py-2 bg-success/10 text-success rounded-lg text-xs font-bold border border-success/20">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    {t('chartDetail.live')}
                                </span>
                            )
                        ) : activeTab === 'settings' && databaseId ? (
                            <WriteGuard>
                                <button
                                    type="button"
                                    onClick={handleSaveReportNotes}
                                    disabled={reportSaveState === 'saving' || settingsLoading}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-primary-600 text-white border border-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {reportSaveState === 'saving' ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Save className="w-3.5 h-3.5" />
                                    )}
                                    {reportSaveState === 'saved'
                                        ? t('chartDetail.saved')
                                        : reportSaveState === 'error'
                                          ? t('common:retry')
                                          : t('common:save')}
                                </button>
                            </WriteGuard>
                        ) : null}
                    </div>

                    <ReadOnlyBanner className="mx-4 mt-3" />
                    <div className="flex-1 relative overflow-hidden flex flex-col min-h-0">
                        {activeTab === 'code' ? (
                            <div className="flex-1 flex flex-col min-h-0">
                                <label className="shrink-0 flex items-center gap-2 px-6 pt-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                    <CodeIcon className="w-3.5 h-3.5 text-primary-400" />
                                    {t('chartDetail.component')}
                                </label>
                                <div className="flex-1 relative min-h-0">
                                    <textarea
                                        value={componentCode}
                                        onChange={canWrite ? (e) => setComponentCode(e.target.value) : undefined}
                                        readOnly={!canWrite}
                                        spellCheck="false"
                                        className={editorTextareaClass}
                                    />
                                </div>
                            </div>
                        ) : activeTab === 'data' ? (
                            <div className="flex-1 flex flex-col min-h-0">
                                <label className="shrink-0 flex items-center justify-between px-6 pt-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                    <span className="flex items-center gap-2">
                                        <Braces className="w-3.5 h-3.5 text-primary-400" />
                                        {t('chartDetail.tabs.data')}
                                    </span>
                                    <span className="normal-case font-medium text-gray-600">
                                        {propsDriven ? t('chartDetail.propsFixedFromCatalog') : t('chartDetail.dataAndOptions')}
                                    </span>
                                </label>
                                <div className="flex-1 relative min-h-0">
                                    <textarea
                                        value={dataText}
                                        onChange={canWrite ? (e) => handleDataChange(e.target.value) : undefined}
                                        readOnly={!canWrite}
                                        spellCheck="false"
                                        className={`${editorTextareaClass}${dataError ? ' text-danger/80' : ''}`}
                                        placeholder='{ "data": { "labels": [], "datasets": [] } }'
                                    />
                                </div>
                                {dataError && (
                                    <div className="shrink-0 mx-4 mb-4 px-3 py-2 rounded-lg bg-danger/10 border border-danger/20 text-danger text-[11px] font-mono flex items-start gap-2">
                                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                        <span className="break-all">{dataError}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="h-full flex flex-col gap-4 p-6 min-h-0">
                                <div className="shrink-0">
                                    <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                                        <Database className="w-3.5 h-3.5 text-primary-400" />
                                        {t('chartDetail.database')}
                                    </label>
                                    <select
                                        value={databaseId || ''}
                                        onChange={handleDatabaseChange}
                                        disabled={databasesLoading}
                                        className="w-full rounded-lg bg-white/5 border border-white/10 text-gray-200 text-[13px] px-3 py-2.5 outline-none focus:border-primary-500/50 disabled:opacity-50"
                                    >
                                        <option value="">
                                            {databasesLoading ? t('chartDetail.loadingEllipsis') : t('chartDetail.selectDatabase')}
                                        </option>
                                        {databases.map((db) => (
                                            <option key={db.id} value={String(db.id)}>
                                                {db.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1 flex flex-col min-h-0 gap-2">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 shrink-0">
                                        {t('chartDetail.notes')}
                                    </label>
                                    <textarea
                                        value={reportSettingsMap[chart.id] ?? ''}
                                        onChange={canWrite ? (e) => handleReportNoteChange(e.target.value) : undefined}
                                        readOnly={!canWrite}
                                        spellCheck="false"
                                        disabled={!databaseId || settingsLoading}
                                        placeholder={
                                            !canWrite
                                                ? t('chartDetail.noNoteAdded')
                                                : databaseId
                                                ? t('chartDetail.addDescriptionPlaceholder')
                                                : t('chartDetail.selectDatabaseToAddNotes')
                                        }
                                        className="flex-1 w-full min-h-[200px] rounded-lg bg-white/5 border border-white/10 text-gray-200 text-[13px] px-3 py-3 outline-none focus:border-primary-500/50 resize-none font-mono leading-relaxed disabled:opacity-45 disabled:cursor-not-allowed"
                                    />
                                    {settingsLoading && databaseId && (
                                        <p className="text-[11px] text-gray-500 flex items-center gap-1 shrink-0">
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            {t('chartDetail.loadingEllipsis')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="xl:col-span-7 bg-white rounded-3xl p-10 shadow-xl shadow-gray-200/50 border border-gray-100 min-h-[600px] flex flex-col">
                    <div className="flex items-center justify-between mb-10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-600">
                                <Layout className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900 tracking-tight">{t('chartDetail.livePreview')}</h2>
                                <p className="text-xs text-gray-400 font-medium italic">{t('chartDetail.synchronizedWithDataEditor')}</p>
                            </div>
                        </div>
                        <div className="px-3 py-1 bg-primary-50 text-xxs font-black text-primary-600 rounded-md tracking-widest">
                            {t('chartDetail.interactive')}
                        </div>
                    </div>

                    <div className="flex-1 flex items-center justify-center w-full relative">
                        <div
                            className={`w-full ${
                                isTable
                                    ? `min-h-[400px] max-h-[520px] overflow-y-auto ${CHART_LIBRARY_TABLE_CLASS}`
                                    : 'h-[400px]'
                            } ${isCircular ? 'max-w-md mx-auto' : ''}`}
                        >
                            {ChartComp && (
                                propsDriven ? (
                                    <ChartComp {...previewProps} />
                                ) : (
                                    <ChartComp data={previewConfig.data} options={previewConfig.options} />
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
                </div>
            </div>
        </div>
    );
};
